import { useState } from "react";
import { Check, ChevronsUpDown, X, Plus, Filter } from "lucide-react";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { FilterCondition, FilterFieldConfig } from "./types";

// Shared, reusable condition-filter builder (monday-style). Domain-agnostic:
// callers pass a `fields` config; rows are AND-joined; the caller compiles
// `conditions` to its own backend query. Use this anywhere the app needs a
// consistent, composable filter (audits, content planner, keyword research…).

let _seq = 0;
export function newConditionId(): string {
	_seq += 1;
	return `cond-${_seq}`;
}

export function isActiveCondition(c: FilterCondition): boolean {
	return Boolean(c.value && c.value.trim());
}

export function emptyConditionFor(fields: FilterFieldConfig[]): FilterCondition {
	const f = fields[0];
	return {
		id: newConditionId(),
		field: f.key,
		operator: f.operators[0]?.value ?? "",
		value: "",
	};
}

const selectClass =
	"h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral";

interface ConditionFilterProps {
	fields: FilterFieldConfig[];
	conditions: FilterCondition[];
	onChange: (next: FilterCondition[]) => void;
	onClear?: () => void;
	/** Optional label shown in the header (default "Filter"). */
	title?: string;
	/** Drop the standalone card chrome so it can embed as a table toolbar. */
	bare?: boolean;
}

export function ConditionFilter({
	fields,
	conditions,
	onChange,
	onClear,
	title = "Filter",
	bare = false,
}: ConditionFilterProps) {
	const [openValueSelectId, setOpenValueSelectId] = useState<string | null>(null);
	const fieldOf = (key: string) =>
		fields.find((f) => f.key === key) ?? fields[0];

	const update = (id: string, patch: Partial<FilterCondition>) => {
		onChange(
			conditions.map((c) => {
				if (c.id !== id) return c;
				const next = { ...c, ...patch };
				if (patch.field && patch.field !== c.field) {
					// reset operator + value to the new field's defaults
					next.operator = fieldOf(patch.field).operators[0]?.value ?? "";
					next.value = "";
					setOpenValueSelectId(null);
				}
				return next;
			}),
		);
	};

	const remove = (id: string) => onChange(conditions.filter((c) => c.id !== id));
	const add = () => onChange([...conditions, emptyConditionFor(fields)]);
	const activeCount = conditions.filter(isActiveCondition).length;

	return (
		<div className={bare ? "" : "rounded-xl border border-border bg-card p-3"}>
			<div className={cn("flex items-center justify-between gap-2", conditions.length > 0 && "mb-2")}>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<div className="flex items-center gap-2 text-sm font-medium text-foreground">
						<Filter className="h-4 w-4 text-muted-foreground" />
						{title}
						{activeCount > 0 && (
							<span className="rounded-full bg-coral-light px-2 py-0.5 text-xs font-medium text-coral-dark">
								{activeCount} active
							</span>
						)}
					</div>
					<button
						type="button"
						onClick={add}
						className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-coral hover:bg-coral-light"
					>
						<Plus className="h-4 w-4" /> Add condition
					</button>
					{/* Sits next to "Add condition" — beside the controls it acts on —
					    rather than adrift at the far end of the header. Styled as a
					    real button with an X so it reads as an action, not a caption. */}
					{conditions.length > 0 && onClear && (
						<button
							type="button"
							onClick={onClear}
							className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm font-medium text-muted-foreground hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
						>
							<X className="h-4 w-4" /> Clear all
						</button>
					)}
				</div>
			</div>

			<div className="space-y-2">
				{conditions.map((c, i) => {
					const field = fieldOf(c.field);
					return (
						<div key={c.id} className="flex flex-wrap items-center gap-2">
							<span
								className={cn(
									"w-10 text-xs font-medium uppercase tracking-wide",
									i === 0 ? "text-muted-foreground" : "text-coral-dark",
								)}
							>
								{i === 0 ? "Where" : "and"}
							</span>

							<select
								aria-label="Field"
								className={selectClass}
								value={c.field}
								onChange={(e) => update(c.id, { field: e.target.value })}
							>
								{fields.map((f) => (
									<option key={f.key} value={f.key}>
										{f.label}
									</option>
								))}
							</select>

							<select
								aria-label="Operator"
								className={selectClass}
								value={c.operator}
								onChange={(e) => update(c.id, { operator: e.target.value })}
							>
								{field.operators.map((op) => (
									<option key={op.value} value={op.value}>
										{op.label}
									</option>
								))}
							</select>

							{field.valueInput.kind === "select" && field.valueInput.searchable ? (
								<Popover
									open={openValueSelectId === c.id}
									onOpenChange={(open) =>
										setOpenValueSelectId(open ? c.id : null)
									}
								>
									<PopoverTrigger asChild>
										<button
											type="button"
											aria-label="Value"
											role="combobox"
											aria-expanded={openValueSelectId === c.id}
											className={cn(
												selectClass,
												"inline-flex min-w-[12rem] items-center justify-between gap-2 text-left",
												!c.value && "text-muted-foreground",
											)}
										>
											<span className="truncate">
												{field.valueInput.options.find((o) => o.value === c.value)
													?.label ??
													field.valueInput.placeholder ??
													"Select..."}
											</span>
											<ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
										</button>
									</PopoverTrigger>
									<PopoverContent className="w-[18rem] p-0" align="start">
										<Command>
											<CommandInput
												placeholder={
													field.valueInput.searchPlaceholder ?? "Search options..."
												}
												className="h-10"
											/>
											<CommandList>
												<CommandEmpty>
													{field.valueInput.emptyMessage ?? "No option found."}
												</CommandEmpty>
												<CommandGroup>
													<CommandItem
														value="select empty clear"
														onSelect={() => {
															update(c.id, { value: "" });
															setOpenValueSelectId(null);
														}}
													>
														<Check
															className={cn(
																"mr-2 h-4 w-4",
																!c.value ? "opacity-100" : "opacity-0",
															)}
														/>
														{field.valueInput.placeholder ?? "Select..."}
													</CommandItem>
													{field.valueInput.options.map((o) => (
														<CommandItem
															key={o.value}
															value={[o.label, o.value, o.searchValue]
																.filter(Boolean)
																.join(" ")}
															onSelect={() => {
																update(c.id, { value: o.value });
																setOpenValueSelectId(null);
															}}
														>
															<Check
																className={cn(
																	"mr-2 h-4 w-4",
																	c.value === o.value
																		? "opacity-100"
																		: "opacity-0",
																)}
															/>
															{o.label}
														</CommandItem>
													))}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							) : field.valueInput.kind === "select" ? (
								<select
									aria-label="Value"
									className={cn(selectClass, "min-w-[12rem]")}
									value={c.value}
									onChange={(e) => update(c.id, { value: e.target.value })}
								>
									<option value="">Select…</option>
									{field.valueInput.options.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
							) : (
								<input
									aria-label="Value"
									type="text"
									className={cn(selectClass, "min-w-[14rem]")}
									placeholder={field.valueInput.placeholder ?? "contains text…"}
									value={c.value}
									onChange={(e) => update(c.id, { value: e.target.value })}
								/>
							)}

							<button
								type="button"
								aria-label="Remove condition"
								onClick={() => remove(c.id)}
								// No ml-auto: the X sits directly after the value input it
								// clears, instead of being flung to the row's far edge where
								// it reads as unrelated to the condition.
								className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
							>
								<X className="h-4 w-4" />
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
}
